import { useState, Fragment } from 'react';
import { Tab } from '@headlessui/react';
import OrderCard from '../../../components/dashboard/order/OrderCard';
import { useGetMyOrdersQuery, useGetMerchantOrdersQuery } from '../../../redux/apis/endpoints/orderApi';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const userLang = navigator.language || navigator.userLanguage;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Define FulfillmentGroup statuses for filtering
const FG_ONGOING_STATUSES = [
  'processing',
  'coordinating_pickup',
  'ready_for_pickup',
  'shipped_to_customer',
  'partially_delivered_to_customer',
  'awaiting_return_shipment',
  'awaiting_return_dropoff',
  'return_in_transit_to_merchant',
  'return_processing_by_merchant'
];
const FG_COMPLETED_STATUSES = [
  'pickup_completed_by_customer',
  'delivery_completed_to_customer',
  'return_completed_for_group',
  'partial_return_completed',
  'group_return_completed',
  'return_completed'
];
const FG_CANCELED_STATUS = 'canceled';

const ProfileTransactions = () => {
  const [activeRoleTab, setActiveRoleTab] = useState('all');
  const [activeStatusTab, setActiveStatusTab] = useState('all');

  const { 
    data: myOrdersRaw = [], 
    isLoading: isLoadingMyOrders, 
    isFetching: isFetchingMyOrders, 
    error: errorMyOrders 
  } = useGetMyOrdersQuery();
  
  const { 
    data: merchantOrdersRaw = [], 
    isLoading: isLoadingMerchantOrders, 
    isFetching: isFetchingMerchantOrders, 
    error: errorMerchantOrders 
  } = useGetMerchantOrdersQuery();

  const isLoading = isLoadingMyOrders || isLoadingMerchantOrders || isFetchingMyOrders || isFetchingMerchantOrders;
  const error = errorMyOrders || errorMerchantOrders;

  // ---> DEBUG: Log raw data before any filtering
  // console.log('[Transactions Raw Data]', { myOrdersRaw, merchantOrdersRaw }); // Removed
  // ---> END DEBUG

  let preliminaryFilteredOrders = [];
  let sourcePrefix = '';

  if (activeRoleTab === 'all') {
    const myOrdersWithPrefix = myOrdersRaw.map(o => ({ ...o, source: 'my' }));
    const merchantOrdersWithPrefix = merchantOrdersRaw.map(o => ({ ...o, source: 'merchant' }));
    preliminaryFilteredOrders = [...myOrdersWithPrefix, ...merchantOrdersWithPrefix];
  } else if (activeRoleTab === 'buying') {
    preliminaryFilteredOrders = myOrdersRaw
      .filter(order => order.orderType === 'purchase' || order.orderType === 'swap')
      .map(o => ({ ...o, source: 'my' }));
    sourcePrefix = 'my-';
  } else if (activeRoleTab === 'selling') {
    preliminaryFilteredOrders = merchantOrdersRaw
      .filter(order => order.orderType === 'purchase' || order.orderType === 'swap')
      .map(o => ({ ...o, source: 'merchant' }));
    sourcePrefix = 'merchant-';
  } else if (activeRoleTab === 'renting') {
    preliminaryFilteredOrders = myOrdersRaw
      .filter(order => order.orderType === 'rental')
      .map(o => ({ ...o, source: 'my' }));
    sourcePrefix = 'my-';
  } else if (activeRoleTab === 'lending') {
    preliminaryFilteredOrders = merchantOrdersRaw
      .filter(order => order.orderType === 'rental')
      .map(o => ({ ...o, source: 'merchant' }));
    sourcePrefix = 'merchant-';
  }

  // ---> DEBUG: Log preliminary filtered orders before grouping
  // console.log(`[Transactions Filter - ${activeRoleTab}] Preliminary Orders Count: ${preliminaryFilteredOrders.length}`, preliminaryFilteredOrders); // Removed
  // ---> END DEBUG

  // Apply status filtering directly to preliminaryFilteredOrders
  const filteredOrders = preliminaryFilteredOrders.filter(order => {
    if (activeStatusTab === 'all') return true;
    // Ensure fulfillmentGroups exists and is an array before trying to use array methods on it.
    if (!order.fulfillmentGroups || !Array.isArray(order.fulfillmentGroups) || order.fulfillmentGroups.length === 0) {
        // If an order has no fulfillment groups, it doesn't match specific status tabs (ongoing, completed, canceled)
        // unless the 'all' tab is selected, which is handled above.
        return false; 
    }

    const fgStatuses = order.fulfillmentGroups.map(fg => fg.status?.toLowerCase());

    if (activeStatusTab === 'ongoing') {
      // At least one FG is not completed or canceled
      return fgStatuses.some(status => !FG_COMPLETED_STATUSES.includes(status) && status !== FG_CANCELED_STATUS);
    }
    
    if (activeStatusTab === 'completed') {
      // All FGs are either completed or canceled AND at least one completed
      const allTerminalOrCanceled = fgStatuses.every(status => FG_COMPLETED_STATUSES.includes(status) || status === FG_CANCELED_STATUS);
      const atLeastOneCompleted = fgStatuses.some(status => FG_COMPLETED_STATUSES.includes(status));
      return allTerminalOrCanceled && atLeastOneCompleted;
    }

    if (activeStatusTab === 'canceled') {
      // Order is considered canceled if it has at least one canceled FG and no ongoing FGs.
      const hasOngoing = fgStatuses.some(status => FG_ONGOING_STATUSES.includes(status));
      const hasCanceled = fgStatuses.some(status => status === FG_CANCELED_STATUS);
      return hasCanceled && !hasOngoing;
    }

    return false;
  });

  const roleTabs = [
    { id: 'all', label: userLang === 'zh-CN' ? '全部' : 'All' },
    { id: 'buying', label: userLang === 'zh-CN' ? '正在购买' : 'Buying' },
    { id: 'selling', label: userLang === 'zh-CN' ? '正在销售' : 'Selling' },
    { id: 'renting', label: userLang === 'zh-CN' ? '正在租赁' : 'Renting' },
    { id: 'lending', label: userLang === 'zh-CN' ? '正在出租' : 'Lending' },
  ];

  const statusTabs = [
    { id: 'all', label: userLang === 'zh-CN' ? '全部' : 'All' },
    { id: 'ongoing', label: userLang === 'zh-CN' ? '进行中' : 'Ongoing' },
    { id: 'completed', label: userLang === 'zh-CN' ? '已完成' : 'Completed' },
    { id: 'canceled', label: userLang === 'zh-CN' ? '已取消' : 'Canceled' },
  ];

  // const ongoingStatuses = ['pending', 'processing', 'shipped', 'in_use', 'return_pending', 'return_shipped']; // Removed
  // const terminalStatuses = ['delivered', 'completed', 'returned', 'return_delivered']; // Removed

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[300px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        <p>{userLang === 'zh-CN' ? '加载记录时出错：' : 'Error loading transactions:'}</p>
        <p>{error.message || JSON.stringify(error)}</p>
      </div>
    );
  }

  const getEmptyStateMessage = () => {
    const roleTextMap = {
        all: userLang === 'zh-CN' ? '任何' : 'any',
        buying: userLang === 'zh-CN' ? '购买' : 'buying',
        selling: userLang === 'zh-CN' ? '销售' : 'selling',
        renting: userLang === 'zh-CN' ? '租赁' : 'renting',
        lending: userLang === 'zh-CN' ? '出租' : 'lending',
    };
    const statusText = statusTabs.find(t => t.id === activeStatusTab)?.label || activeStatusTab;
    const roleText = roleTextMap[activeRoleTab] || activeRoleTab;

    return userLang === 'zh-CN'
      ? `您没有${statusText}状态的${roleText}记录。`
      : `You have no ${statusText.toLowerCase()} ${roleText} transactions.`;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {userLang === 'zh-CN' ? '交易记录' : 'Transactions'}
        </h2>
      </div>

      <Tab.Group defaultIndex={0} onChange={(index) => setActiveRoleTab(roleTabs[index].id)}>
        <Tab.List className="flex space-x-1 sm:space-x-4 border-b border-gray-200 overflow-x-auto mb-4">
          {roleTabs.map((tab) => (
            <Tab
              key={tab.id}
              className={({ selected }) =>
                classNames(
                  'px-3 py-2.5 text-sm font-medium leading-5 whitespace-nowrap',
                  'focus:outline-none',
                  selected
                    ? 'text-brand-teal border-b-2 border-brand-teal'
                    : 'text-gray-500 hover:text-gray-700 border-b-2 border-transparent'
                )
              }
            >
              {tab.label}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-2">
          {roleTabs.map((roleTab) => (
            <Tab.Panel key={roleTab.id} className={classNames('focus:outline-none')}>
              <Tab.Group defaultIndex={0} onChange={(index) => setActiveStatusTab(statusTabs[index].id)}>
                <div className="mb-6 flex flex-wrap gap-2">
                  {statusTabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      as={Fragment}
                    >
                     {({ selected }) => (
                        <button
                          className={`
                            px-3 py-1 rounded-full border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal
                            ${selected
                              ? 'border-brand-teal bg-brand-teal text-white'
                              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }
                          `}
                        >
                          {tab.label}
                        </button>
                      )}
                    </Tab>
                  ))}
                </div>
                <Tab.Panels className="mt-6">
                  {statusTabs.map((statusTab) => (
                    <Tab.Panel key={statusTab.id} className={classNames('focus:outline-none')}>
                      {filteredOrders.length === 0 ? (
                        <div className="text-center py-12">
                          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            {userLang === 'zh-CN' ? '暂无记录' : 'No transactions found'}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {getEmptyStateMessage()} 
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {filteredOrders.map((order) => (
                            <OrderCard 
                              key={`${order.source || sourcePrefix}${order.id}`} 
                              order={order} 
                              orderId={order.id} 
                              viewContext={activeRoleTab} 
                            />
                          ))}
                        </div>
                      )}
                    </Tab.Panel>
                  ))}
                </Tab.Panels>
              </Tab.Group>
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>

    </div>
  );
};

export default ProfileTransactions;
